import React, { useState, useEffect, useCallback } from 'react';
import { financeDashboardService } from '../../services/financeService';
import './Financial.css';

const fmt = (n) => n != null ? Number(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
const today = () => new Date().toISOString().split('T')[0];
const startOfYear = () => `${new Date().getFullYear()}-01-01`;

const TYPE_LABELS = {
  ASSET: 'Asset', LIABILITY: 'Liability', EQUITY: 'Equity',
  REVENUE: 'Revenue', EXPENSE: 'Expense',
  CONTRA_ASSET: 'Contra-Asset', CONTRA_LIABILITY: 'Contra-Liability',
  CONTRA_EQUITY: 'Contra-Equity', CONTRA_REVENUE: 'Contra-Revenue', CONTRA_EXPENSE: 'Contra-Expense',
};

/**
 * Chart of Accounts / Trial Balance / Profit & Loss — all three views over the same
 * double-entry ledger that every Purchase, Invoice, Payment, Expense, and Batch cost
 * now posts to automatically.
 */
const LedgerManagement = () => {
  const [tab, setTab] = useState('accounts'); // accounts | trial-balance | pnl
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [trialBalance, setTrialBalance] = useState([]);
  const [asOfDate, setAsOfDate] = useState(today());
  const [pnl, setPnl] = useState(null);
  const [pnlStart, setPnlStart] = useState(startOfYear());
  const [pnlEnd, setPnlEnd] = useState(today());

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeDashboardService.getChartOfAccounts();
      setAccounts(res?.data || res || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTrialBalance = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeDashboardService.getTrialBalance(date);
      setTrialBalance(res?.data || res || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPnl = useCallback(async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeDashboardService.getProfitLoss(start, end);
      setPnl(res?.data || res || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'accounts') loadAccounts();
    if (tab === 'trial-balance') loadTrialBalance(asOfDate);
    if (tab === 'pnl') loadPnl(pnlStart, pnlEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const trialTotals = trialBalance.reduce((acc, e) => ({
    debit: acc.debit + (Number(e.debitTotal) || 0),
    credit: acc.credit + (Number(e.creditTotal) || 0),
  }), { debit: 0, credit: 0 });
  const trialBalanced = Math.abs(trialTotals.debit - trialTotals.credit) < 0.01;

  const renderAccountRow = (acc, depth) => (
    <React.Fragment key={acc.id}>
      <tr style={{ borderBottom: '1px solid var(--color-bg-tertiary)', background: depth === 0 ? 'var(--color-neutral-50)' : '#fff' }}>
        <td style={{ padding: '8px 10px', fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)' }}>{acc.accountCode}</td>
        <td style={{ padding: '8px 10px', paddingLeft: 10 + depth * 22, fontWeight: depth === 0 ? 700 : 500 }}>
          {acc.accountName}
          {acc.isSystemAccount && depth === 0 && (
            <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--color-text-tertiary)', fontWeight: 400 }}>category</span>
          )}
        </td>
        <td style={{ padding: '8px 10px' }}>
          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{TYPE_LABELS[acc.accountType] || acc.accountType}</span>
        </td>
        <td style={{ padding: '8px 10px', fontSize: 11, color: acc.normalBalance === 'DEBIT' ? 'var(--color-info)' : 'var(--color-warning)' }}>
          {acc.normalBalance === 'DEBIT' ? 'Debit' : 'Credit'}
        </td>
        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: depth === 0 ? 700 : 500 }}>
          {acc.isPostable ? `PKR ${fmt(acc.currentBalance)}` : ''}
        </td>
      </tr>
      {(acc.children || []).map(child => renderAccountRow(child, depth + 1))}
    </React.Fragment>
  );

  return (
    <div className="financial-container">
      <div className="page-header">
        <div>
          <h2>Chart of Accounts</h2>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, margin: 0 }}>
            The general ledger every Purchase, Invoice, Payment, Expense, and batch cost posts to automatically.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid var(--color-border-medium)' }}>
        {[
          { key: 'accounts', label: 'Accounts' },
          { key: 'trial-balance', label: 'Trial Balance' },
          { key: 'pnl', label: 'Profit & Loss' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 16px', border: 'none', borderBottom: tab === t.key ? '2px solid var(--color-accent-hover)' : '2px solid transparent',
              background: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
              color: tab === t.key ? 'var(--color-accent-hover)' : 'var(--color-text-secondary)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="alert-error" style={{ marginBottom: 12 }}>{error}</div>}
      {loading && <div style={{ padding: 20, color: 'var(--color-text-secondary)' }}>Loading…</div>}

      {!loading && tab === 'accounts' && (
        <div className="table-container">
          <table className="data-table" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Account</th>
                <th>Type</th>
                <th>Normal Balance</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0
                ? <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No accounts found.</td></tr>
                : accounts.map(acc => renderAccountRow(acc, 0))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && tab === 'trial-balance' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
            <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>As of</label>
            <input type="date" className="form-control" style={{ maxWidth: 180 }} value={asOfDate}
              onChange={e => { setAsOfDate(e.target.value); loadTrialBalance(e.target.value); }} />
            <span style={{
              fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 9999,
              background: trialBalanced ? 'var(--color-success-light)' : 'var(--color-error-light)', color: trialBalanced ? 'var(--color-success)' : 'var(--color-error)',
            }}>
              {trialBalanced ? '✓ Balanced' : '⚠ Out of balance'}
            </span>
          </div>
          <div className="table-container">
            <table className="data-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Debit</th>
                  <th style={{ textAlign: 'right' }}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {trialBalance.length === 0
                  ? <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No activity posted yet.</td></tr>
                  : trialBalance.map(e => (
                    <tr key={e.accountId} style={{ borderBottom: '1px solid var(--color-bg-tertiary)' }}>
                      <td style={{ padding: '8px 10px', fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)' }}>{e.accountCode}</td>
                      <td style={{ padding: '8px 10px' }}>{e.accountName}</td>
                      <td style={{ padding: '8px 10px', fontSize: 11, color: 'var(--color-text-secondary)' }}>{TYPE_LABELS[e.accountType] || e.accountType}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(e.debitTotal) > 0 ? fmt(e.debitTotal) : ''}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(e.creditTotal) > 0 ? fmt(e.creditTotal) : ''}</td>
                    </tr>
                  ))}
              </tbody>
              {trialBalance.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--color-border-medium)' }}>
                    <td colSpan={3} style={{ padding: '10px', fontWeight: 700, textAlign: 'right' }}>Total:</td>
                    <td style={{ padding: '10px', fontWeight: 700, textAlign: 'right' }}>{fmt(trialTotals.debit)}</td>
                    <td style={{ padding: '10px', fontWeight: 700, textAlign: 'right' }}>{fmt(trialTotals.credit)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {!loading && tab === 'pnl' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>From</label>
            <input type="date" className="form-control" style={{ maxWidth: 180 }} value={pnlStart} onChange={e => setPnlStart(e.target.value)} />
            <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>To</label>
            <input type="date" className="form-control" style={{ maxWidth: 180 }} value={pnlEnd} onChange={e => setPnlEnd(e.target.value)} />
            <button className="btn-primary" onClick={() => loadPnl(pnlStart, pnlEnd)}>Run Report</button>
          </div>

          {pnl && (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                <div className="metric-card revenue" style={{ flex: 1, minWidth: 200 }}>
                  <div className="metric-content">
                    <span className="metric-label">Total Revenue</span>
                    <span className="metric-value">PKR {fmt(pnl.totalRevenue)}</span>
                  </div>
                </div>
                <div className="metric-card expenses" style={{ flex: 1, minWidth: 200 }}>
                  <div className="metric-content">
                    <span className="metric-label">Total Expenses</span>
                    <span className="metric-value">PKR {fmt(pnl.totalExpenses)}</span>
                  </div>
                </div>
                <div className="metric-card profit" style={{ flex: 1, minWidth: 200 }}>
                  <div className="metric-content">
                    <span className="metric-label">Net Profit</span>
                    <span className="metric-value" style={{ color: Number(pnl.netProfit) >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
                      PKR {fmt(pnl.netProfit)}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div>
                  <h4 style={{ fontSize: 14, marginBottom: 8, color: 'var(--color-neutral-700)' }}>Revenue by Account</h4>
                  <div className="table-container">
                    <table className="data-table" style={{ fontSize: 13 }}>
                      <tbody>
                        {(pnl.revenueDetails || []).length === 0
                          ? <tr><td style={{ padding: 14, color: 'var(--color-text-tertiary)' }}>No revenue posted in this period.</td></tr>
                          : pnl.revenueDetails.map(r => (
                            <tr key={r.accountCode} style={{ borderBottom: '1px solid var(--color-bg-tertiary)' }}>
                              <td style={{ padding: '7px 10px' }}>{r.accountName}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--color-success)' }}>{fmt(r.balance)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h4 style={{ fontSize: 14, marginBottom: 8, color: 'var(--color-neutral-700)' }}>Expenses by Account</h4>
                  <div className="table-container">
                    <table className="data-table" style={{ fontSize: 13 }}>
                      <tbody>
                        {(pnl.expenseDetails || []).length === 0
                          ? <tr><td style={{ padding: 14, color: 'var(--color-text-tertiary)' }}>No expenses posted in this period.</td></tr>
                          : pnl.expenseDetails.map(r => (
                            <tr key={r.accountCode} style={{ borderBottom: '1px solid var(--color-bg-tertiary)' }}>
                              <td style={{ padding: '7px 10px' }}>{r.accountName}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--color-warning)' }}>{fmt(r.balance)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default LedgerManagement;
